import React, { useCallback, useEffect, useState } from "react";
import AiFailureAnalysis from "./AiFailureAnalysis";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Stack,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useDispatch } from "react-redux";
import {
  fetchReportStructure,
  fetchReportFile,
} from "../../../features/slices/Report";
import { useNotification } from "../../../context/NotificationContext";
import { AppDispatch } from "../../../store";
import RichTreeView, { TreeNode } from "./RichTreeView";

interface SelectedFile {
  path: string;
  content: string;
  binary?: boolean;
  truncated?: boolean;
  message?: string;
  size?: number;
  image?: boolean;
  mime?: string;
}

interface ReportBrowserProps {
  caseId: number;
  reportExists?: boolean;
  /** When true, fetch the structure immediately (used inside an expanded panel). */
  autoLoad?: boolean;
  /** Render inside a Paper card with header + Browse button (CaseView). */
  framed?: boolean;
}

const ReportBrowser: React.FC<ReportBrowserProps> = ({
  caseId,
  reportExists = true,
  autoLoad = false,
  framed = true,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useNotification();
  const [caseStructure, setCaseStructure] = useState<TreeNode[]>([]);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);

  const handleShowStructure = useCallback(async () => {
    setLoadingStructure(true);
    try {
      const result = await dispatch(fetchReportStructure(caseId)).unwrap();
      setCaseStructure(result.structure);
    } catch (error) {
      console.error("Error fetching report structure:", error);
      notify("Failed to fetch report structure.", "error");
    } finally {
      setLoadingStructure(false);
    }
  }, [caseId, dispatch, notify]);

  useEffect(() => {
    if (autoLoad && reportExists) handleShowStructure();
  }, [autoLoad, reportExists, handleShowStructure]);

  const handleFileClick = async (path: string) => {
    setLoadingFile(true);
    setSelectedFile({ path, content: "" });
    try {
      const result = await dispatch(
        fetchReportFile({ caseId, path })
      ).unwrap();
      setSelectedFile(result);
    } catch (error) {
      console.error("Error fetching file:", error);
      notify("Failed to open file.", "error");
      setSelectedFile({ path, content: "", message: "Could not load this file." });
    } finally {
      setLoadingFile(false);
    }
  };

  const copyContent = () => {
    if (selectedFile?.content) {
      navigator.clipboard.writeText(selectedFile.content);
      notify("Copied to clipboard.", "success");
    }
  };

  const browser = (
    <>
      {reportExists && <AiFailureAnalysis caseId={caseId} />}
      {framed && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: "0.95rem" }}>
            Report contents
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AccountTreeOutlinedIcon />}
            onClick={handleShowStructure}
            disabled={!reportExists || loadingStructure}
          >
            {caseStructure.length > 0 ? "Refresh" : "Browse report"}
          </Button>
        </Stack>
      )}

      {!reportExists && (
        <Alert severity="info" variant="outlined">
          No stored report for this case yet.
        </Alert>
      )}

      {loadingStructure && <LinearProgress sx={{ mb: 2 }} />}

      {caseStructure.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
            gap: 2,
            alignItems: "start",
          }}
        >
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 1,
              maxHeight: 420,
              overflow: "auto",
              bgcolor: "background.level1",
            }}
          >
            <RichTreeView
              data={caseStructure}
              onFileClick={handleFileClick}
              selectedPath={selectedFile?.path ?? null}
            />
          </Box>

          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              minHeight: 180,
              maxHeight: 420,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {!selectedFile ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "text.secondary",
                  p: 3,
                  textAlign: "center",
                }}
              >
                <Typography variant="body2">
                  Select a file on the left to preview it here.
                </Typography>
              </Box>
            ) : (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.level1",
                  }}
                >
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                    }}
                  >
                    {selectedFile.path}
                  </Typography>
                  {typeof selectedFile.size === "number" && (
                    <Chip size="small" label={`${selectedFile.size} B`} variant="outlined" />
                  )}
                  <Tooltip title="Copy">
                    <span>
                      <IconButton
                        size="small"
                        onClick={copyContent}
                        disabled={!selectedFile.content || selectedFile.image}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>

                {loadingFile && <LinearProgress />}

                <Box sx={{ flex: 1, overflow: "auto" }}>
                  {selectedFile.message ? (
                    <Box sx={{ p: 2 }}>
                      <Alert severity="warning" variant="outlined">
                        {selectedFile.message}
                      </Alert>
                    </Box>
                  ) : selectedFile.image ? (
                    <Box
                      sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        // checkerboard so transparent PNGs are visible
                        backgroundColor: "#fff",
                        backgroundImage:
                          "linear-gradient(45deg,#eef2f6 25%,transparent 25%),linear-gradient(-45deg,#eef2f6 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eef2f6 75%),linear-gradient(-45deg,transparent 75%,#eef2f6 75%)",
                        backgroundSize: "16px 16px",
                        backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
                        minHeight: "100%",
                      }}
                    >
                      <Box
                        component="img"
                        alt={selectedFile.path}
                        src={`data:${selectedFile.mime};base64,${selectedFile.content}`}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: 380,
                          objectFit: "contain",
                          borderRadius: 1,
                          boxShadow: "0 2px 10px rgba(16,24,40,0.15)",
                          bgcolor: "#fff",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 2,
                        fontSize: "0.78rem",
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                        lineHeight: 1.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        color: "#1f2937",
                      }}
                    >
                      {selectedFile.content}
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </>
  );

  if (!framed) return browser;
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      {browser}
    </Paper>
  );
};

export default ReportBrowser;
